import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import '@adornis/entity-picker/client/x-multi-entity-picker.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import type { AspectConfig } from '@adornis/filter/filter-options.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { html } from 'lit';
import { translationDict } from '../db/translation-dict.js';
import { GroupsAPI } from './api/GroupsAPI.js';
import { Group } from './entities/Group.js';

const modeDisplay = {
  and: 'in allen Gruppen',
  or: 'in einer Gruppe',
};

@Entity()
export class GroupFilterAspect extends AbstractAspect {
  static override _class = 'GroupFilterAspect';

  @Field(() => String, { default: v => v ?? 'or' })
  mode!: keyof typeof modeDisplay;

  // the filter aspect can work with atomic values or with objects
  // atomic fieldName: value
  // object $elemMatch: { fieldName: { $in: value } }
  // therfore you can filter arrays with objects like the roles array for example or just a single field
  // ! do not expose this to the user. you need to specify it yourself for the corresponding field
  @Field(() => String, { default: v => v ?? 'atomic' })
  operatingFieldType!: 'atomic' | 'object';

  // the _id of the group
  @Field(() => [String], { default: v => v ?? [] }) value: Maybe<string[]>;

  // only used for displaying in the aspect
  @Field(() => String) name: Maybe<string>;

  constructor(doc: PartialEntityData<GroupFilterAspect>) {
    super(doc);
  }

  override getMongoFilter(): object | null {
    if (!this.fieldName || !this.value?.length) return null;

    if (this.operatingFieldType === 'atomic') {
      return this.mode === 'or'
        ? {
            [this.fieldName]: { $in: this.value },
          }
        : {
            [this.fieldName]: { $all: this.value },
          };
    }

    const matchCondition =
      this.mode === 'and'
        ? { $all: this.value.map(id => ({ $elemMatch: { _id: id } })) }
        : { $elemMatch: { _id: { $in: this.value } } };

    return {
      [this.fieldName]: matchCondition,
    };
  }
}

export const GroupFilterAspectSchema: (options?: {
  filter?: AdornisFilter;
  operatingFieldType?: 'atomic' | 'object';
}) => AspectConfig<GroupFilterAspect> = options => ({
  aspectConstructor: GroupFilterAspect,
  renderEditor: ({ aspectValue }) => {
    options ??= { operatingFieldType: 'atomic' };
    options.filter ??= new AdornisFilter({ searchFields: ['name'] });

    if (!options.filter.searchFields?.length) {
      options.filter.searchFields = ['name'];
    }

    return html`
      <div class="flex flex-wrap gap-2">
        <div class="flex flex-1 min-w-[100px]">
          ${DropdownSelectionInput({
            value: aspectValue.mode,
            onPicked: value => {
              aspectValue.mode = value ?? 'or';
            },
            renderString: (key: keyof typeof modeDisplay) => modeDisplay[key],
            selectables: Object.keys(modeDisplay) as (keyof typeof modeDisplay)[],
          })}
        </div>
        <div class="flex flex-1 min-w-[200px]">
          ${EntityPickerInput<typeof Group, true>({
            class: Group,
            operation: GroupsAPI.subscribeGroups,
            multi: true,
            filter: options.filter,
            placeholder: 'beliebig',
            placeholderMode: 'hide',
            renderString: group => `${group.name} (${translationDict._class[group._class]?.[0] as string})`,
            selectionSet: { name: 1, _id: 1 },
            value: aspectValue.value,
            onEntityPicked: values => {
              aspectValue.value = values?.map(g => g._id) ?? [];
              aspectValue.name = values?.map(g => g.name).join(', ') ?? null;

              // This is only used for possible reactivity
              aspectValue.operatingFieldType = options?.operatingFieldType ?? 'atomic';
            },
          })}
        </div>
      </div>
    `;
  },
  renderPreview: ({ aspectValue }) => (aspectValue.name ? html` ${aspectValue.name} ` : null),
});
